'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { BlogCategory, BlogTag, getTags } from '@/lib/services/blogService';

interface BlogSidebarProps {
  categories: BlogCategory[];
  currentCategory?: string;
  currentTag?: string;
}

export default function BlogSidebar({ categories, currentCategory, currentTag }: BlogSidebarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(true);

  useEffect(() => {
    async function fetchTags() {
      try {
        setTagsLoading(true);
        const fetchedTags = await getTags();
        setTags(fetchedTags.slice(0, 8)); // Show only first 8 tags
      } catch (error) {
        console.error('Error fetching tags:', error);
      } finally {
        setTagsLoading(false);
      }
    }
    fetchTags();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/blog?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      router.push('/blog');
    }
  };

  const clearFilters = () => {
    router.push('/blog');
  };

  return (
    <div className="space-y-8">
      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Search</h3>
        <form onSubmit={handleSearch}>
          <div className="relative">
            <label htmlFor="blog-sidebar-search" className="sr-only">Search blog posts</label>
            <input
              id="blog-sidebar-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search blog posts..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
          <button
            type="submit"
            className="w-full mt-3 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Active Filters */}
      {(currentCategory || currentTag || searchParams.get('search')) && (
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-blue-900">Active Filters</h3>
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Clear All
            </button>
          </div>
          <div className="space-y-2">
            {searchParams.get('search') && (
              <div className="flex items-center justify-between bg-white rounded px-3 py-2">
                <span className="text-sm text-gray-700">
                  Search: "{searchParams.get('search')}"
                </span>
                <button
                  onClick={() => {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('search');
                    router.push(`/blog?${newParams.toString()}`);
                  }}
                  type="button"
                  aria-label="Remove search filter"
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {currentCategory && (
              <div className="flex items-center justify-between bg-white rounded px-3 py-2">
                <span className="text-sm text-gray-700">Category: {
                  categories.find(cat => cat.slug === currentCategory)?.name || 'Unknown'
                }</span>
                <button
                  onClick={() => {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('category');
                    router.push(`/blog?${newParams.toString()}`);
                  }}
                  type="button"
                  aria-label="Remove category filter"
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
        <div className="space-y-2">
          <Link
            href="/blog"
            className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
              !currentCategory
                ? 'bg-primary-100 text-primary-800 font-medium'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            All Posts
          </Link>
          {categories.map((category) => (
            <Link
              key={category.id}
              href={`/blog/category/${category.slug}/`}
              className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                currentCategory === category.slug
                  ? 'bg-primary-100 text-primary-800 font-medium'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{category.name}</span>
                <span className="text-xs text-gray-500">({category.post_count ?? 0})</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Need Help?</h3>
        <p className="text-gray-600 text-sm mb-4">
          Have questions about creating the perfect digital card?
        </p>
        <Link
          href="/contact"
          className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm"
        >
          Contact Support
          <svg className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>
    </div>
  );
}